import { animate, prefersReducedMotion as motionPrefersReducedMotion, stagger } from 'motion';

const STAGGER_SEC = 0.05;
const DURATION_SEC = 0.15;
const VERSION_MAP = new WeakMap<HTMLElement, number>();
let nextVersion = 0;

export const prefersReducedMotion = (): boolean => motionPrefersReducedMotion.current === true;

export function fadeInBatch(elements: HTMLElement[]): Promise<unknown> {
  if (elements.length === 0) return Promise.resolve();
  const getDelay = stagger(STAGGER_SEC, { from: 'first' });

  const targets: number[] = [];
  elements.forEach((el) => {
    const startHeight = el.getBoundingClientRect().height;
    el.getAnimations().forEach((a) => a.cancel());
    el.style.display = '';
    el.style.height = '';
    el.style.overflow = '';
    targets.push(el.getBoundingClientRect().height);
    el.style.height = `${startHeight}px`;
    el.style.overflow = 'hidden';
  });

  return Promise.all(
    elements.map((el, i) => {
      const tag = ++nextVersion;
      VERSION_MAP.set(el, tag);
      const cleanup = () => {
        if (VERSION_MAP.get(el) !== tag) return;
        el.style.height = '';
        el.style.overflow = '';
      };
      const controls = animate(
        el,
        { opacity: [null, 1], y: [null, 0], height: [null, `${targets[i]}px`] },
        { duration: DURATION_SEC, delay: getDelay(i, elements.length), ease: 'easeOut' },
      );
      controls.then(cleanup, cleanup);
      return controls;
    }),
  );
}

export function fadeOutBatch(elements: HTMLElement[]): Promise<unknown> {
  if (elements.length === 0) return Promise.resolve();
  const getDelay = stagger(STAGGER_SEC, { from: 'last' });

  elements.forEach((el) => {
    const rect = el.getBoundingClientRect();
    el.getAnimations().forEach((a) => a.cancel());
    el.style.height = `${rect.height}px`;
    el.style.overflow = 'hidden';
  });

  return Promise.all(
    elements.map((el, i) => {
      const tag = ++nextVersion;
      VERSION_MAP.set(el, tag);
      const onComplete = () => {
        if (VERSION_MAP.get(el) !== tag) return;
        el.style.display = 'none';
        el.style.height = '';
        el.style.overflow = '';
      };
      const onCancel = () => {
        if (VERSION_MAP.get(el) !== tag) return;
        el.style.height = '';
        el.style.overflow = '';
      };
      const controls = animate(
        el,
        { opacity: [null, 0], y: [null, '5%'], height: [null, '0px'] },
        { duration: DURATION_SEC, delay: getDelay(i, elements.length), ease: 'easeInOut' },
      );
      controls.then(onComplete, onCancel);
      return controls;
    }),
  );
}
